import { useMemo } from 'react';

import { useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import MuiTable from '@mui/material/Table';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableHead from '@mui/material/TableHead';
import MuiTableRow from '@mui/material/TableRow';

import useGetAllMemberships from '@webclient/components/pages/users/hooks/useGetAllMemberships';
import CONFIG from '@webclient/components/settings/components/tabs/knowledge-base/components/users/config';
import Skeleton from '@webclient/components/settings/components/tabs/knowledge-base/components/users/loading-skeleton';
import { StyledMuiTableHeadCell } from '@webclient/components/settings/components/tabs/knowledge-base/components/users/styles';
import { StyledChip } from '@webclient/components/settings/components/tabs/knowledge-base/components/users/styles';

import calculateFullName from '@webclient/lib/calculateFullName';

import { MinimalUserType } from '@webclient/shared-types/auth';
import { MembershipType } from '@webclient/shared-types/memberships';

import { Text } from '@knowz/knowz-ui';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import sha256 from 'crypto-js/sha256';

function getGravatarUrl(email: string | null): string | null {
  return `https://www.gravatar.com/avatar/${sha256(email)}?s=64&d=retro&r=pg`;
}

export default function Users() {
  const theme = useTheme();

  const { allMembershipsData, areAllMembershipsLoading } = useGetAllMemberships(
    {
      limit: CONFIG.PAGINATION.LIMIT,
      page: 1, // TODO: support infinite scroll?
    },
  );

  const columns = useMemo<ColumnDef<MembershipType>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'user',
        cell: ({ row }) => {
          const user = row.original.user as MinimalUserType;
          const name = user ? calculateFullName(user) : 'John Doe';
          const email = user?.email || row.original.emailInvite;

          return (
            <Stack
              direction="row"
              gap={3}
              alignItems="center"
            >
              <Avatar
                sx={{ width: 40, height: 40 }}
                src={
                  (user?.profilePicture || getGravatarUrl(email)) ?? undefined
                }
                alt={`${name} avatar`}
              />

              <Stack>
                <Text
                  variant="textSm"
                  weight="medium"
                  color={
                    theme.palette[
                      user
                        ? 'Colors/Text/text-primary (900)'
                        : 'Component colors/Utility/Gray/utility-gray-400'
                    ]
                  }
                >
                  {name}
                </Text>
                <Text
                  variant="textXs"
                  color={theme.palette['Colors/Text/text-tertiary (600)']}
                >
                  {email}
                </Text>
              </Stack>
            </Stack>
          );
        },
      },
      {
        header: 'Role',
        accessorKey: 'role',
        cell: ({ getValue }) => (
          <StyledChip
            size="small"
            label={getValue<MembershipType['role']>()}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tableData = useReactTable({
    data: allMembershipsData?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableRowSelection: true,
  });

  return (
    <MuiTable>
      <MuiTableHead>
        <MuiTableRow>
          {tableData?.getHeaderGroups().map((headerGroup, i) => {
            return headerGroup.headers.map((header) => {
              return (
                <StyledMuiTableHeadCell
                  align={
                    CONFIG.COLUMN_STYLES[header.column.id]?.align || 'left'
                  }
                  key={header.column.id}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </StyledMuiTableHeadCell>
              );
            });
          })}
        </MuiTableRow>
      </MuiTableHead>

      <MuiTableBody>
        {areAllMembershipsLoading ? (
          <Skeleton
            headItemsConfig={tableData
              .getAllColumns()
              .map((column) => CONFIG.COLUMN_STYLES[column.id])}
            headItems={tableData.getAllColumns().map((column) => column.id)}
            numberOfRows={CONFIG.PAGINATION.LIMIT}
          />
        ) : (
          tableData.getRowModel().rows.map((row) => (
            <MuiTableRow key={row.original.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <MuiTableCell
                    key={cell.id}
                    sx={{
                      ...(CONFIG.COLUMN_STYLES[cell.column.id]?.width
                        ? {
                            width: CONFIG.COLUMN_STYLES[cell.column.id]?.width,
                          }
                        : {}),
                    }}
                    align={
                      CONFIG.COLUMN_STYLES[cell.column.id]?.align || 'left'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </MuiTableCell>
                );
              })}
            </MuiTableRow>
          ))
        )}
      </MuiTableBody>
    </MuiTable>
  );
}
